export interface HazardData {
  id: string
  name: string
  type: 'natural' | 'port' | 'political' | 'infrastructure'
  subtype: string
  lat: number
  lng: number
  severity: 'low' | 'medium' | 'high' | 'critical'
  description: string
  startDate: string
  estimatedEndDate?: string
  radius: number // Affected area radius in km
  affectedSuppliers?: string[] // IDs of affected suppliers
  impactParameters?: {
    [key: string]: number | string
  }
}
export const mockHazardsData: HazardData[] = [
  {
    id: 'H001',
    name: 'Hurricane Florence',
    type: 'natural',
    subtype: 'hurricane',
    lat: 35.2271,
    lng: -80.8431,
    severity: 'high',
    description:
      'Category 3 hurricane causing severe flooding and power outages',
    startDate: '2023-10-15',
    estimatedEndDate: '2023-10-22',
    radius: 300,
    affectedSuppliers: ['S012', 'S014'],
    impactParameters: {
      windSpeed: '120 mph',
      rainfall: '30 inches',
      floodingRisk: 'severe',
      powerOutage: '85%',
    },
  },
  {
    id: 'H002',
    name: 'Port of Shanghai Congestion',
    type: 'port',
    subtype: 'congestion',
    lat: 31.2304,
    lng: 121.4737,
    severity: 'medium',
    description:
      'Significant delays due to port congestion and labor shortages',
    startDate: '2023-09-28',
    estimatedEndDate: '2023-11-15',
    radius: 50,
    affectedSuppliers: ['S010', 'S020'],
    impactParameters: {
      delayTime: '7-10 days',
      containerBacklog: '20,000 TEU',
      vesselWaitTime: '72 hours',
    },
  },
  {
    id: 'H003',
    name: 'Earthquake in Japan',
    type: 'natural',
    subtype: 'earthquake',
    lat: 35.6762,
    lng: 139.6503,
    severity: 'critical',
    description:
      'Magnitude 6.8 earthquake affecting infrastructure and manufacturing',
    startDate: '2023-10-12',
    radius: 150,
    affectedSuppliers: ['S003', 'S009'],
    impactParameters: {
      magnitude: '6.8',
      infrastructureDamage: 'severe',
      powerOutage: '60%',
      transportDisruption: 'major',
    },
  },
  {
    id: 'H004',
    name: 'Trade Dispute',
    type: 'political',
    subtype: 'tariffs',
    lat: 38.9072,
    lng: -77.0369,
    severity: 'medium',
    description: 'New tariffs affecting semiconductor and electronics imports',
    startDate: '2023-09-01',
    radius: 500,
    affectedSuppliers: ['S001', 'S013', 'S019'],
    impactParameters: {
      tariffIncrease: '25%',
      affectedCategories: 'semiconductors, electronics',
      estimatedCostImpact: '$150M',
    },
  },
  {
    id: 'H005',
    name: 'Highway Closure',
    type: 'infrastructure',
    subtype: 'transportation',
    lat: 48.1351,
    lng: 11.582,
    severity: 'low',
    description: 'Major highway closure due to construction work',
    startDate: '2023-10-10',
    estimatedEndDate: '2023-11-20',
    radius: 80,
    affectedSuppliers: ['S002', 'S007'],
    impactParameters: {
      detourTime: '2-3 hours',
      alternativeRoutes: 'limited',
      freightDelays: 'moderate',
    },
  },
  // New hazards in Europe
  {
    id: 'H006',
    name: 'Port of Rotterdam Strike',
    type: 'port',
    subtype: 'labor strike',
    lat: 51.9244,
    lng: 4.4777,
    severity: 'high',
    description: 'Worker strike affecting container processing and shipping',
    startDate: '2023-10-18',
    estimatedEndDate: '2023-10-28',
    radius: 60,
    affectedSuppliers: ['E022', 'E006', 'E023'],
    impactParameters: {
      operationalCapacity: '30%',
      containerBacklog: '15,000 TEU',
      estimatedLosses: '€2.5M per day',
    },
  },
  {
    id: 'H007',
    name: 'Flooding in Northern Italy',
    type: 'natural',
    subtype: 'flooding',
    lat: 45.4642,
    lng: 9.19,
    severity: 'medium',
    description:
      'Severe flooding affecting transportation and manufacturing facilities',
    startDate: '2023-10-05',
    estimatedEndDate: '2023-10-25',
    radius: 120,
    affectedSuppliers: ['S016', 'E005', 'E020', 'E021'],
    impactParameters: {
      rainfallAmount: '250mm',
      infrastructureDamage: 'moderate',
      roadClosures: '30+',
      evacuationOrders: 'partial',
    },
  },
  {
    id: 'H008',
    name: 'EU-UK Trade Regulations',
    type: 'political',
    subtype: 'regulations',
    lat: 50.8503,
    lng: 4.3517,
    severity: 'medium',
    description:
      'New regulatory framework affecting cross-border trade between EU and UK',
    startDate: '2023-09-15',
    radius: 400,
    affectedSuppliers: ['E002', 'E015', 'E016', 'E017'],
    impactParameters: {
      customsDelays: '24-48 hours',
      additionalDocumentation: 'extensive',
      complianceCosts: 'increased by 15%',
    },
  },
  {
    id: 'H009',
    name: 'Power Grid Failure',
    type: 'infrastructure',
    subtype: 'power outage',
    lat: 52.52,
    lng: 13.405,
    severity: 'high',
    description:
      'Major power grid failure affecting industrial zones in Berlin area',
    startDate: '2023-10-20',
    estimatedEndDate: '2023-10-22',
    radius: 70,
    affectedSuppliers: ['E003', 'E007'],
    impactParameters: {
      affectedArea: '230 sq km',
      populationImpacted: '1.2M',
      industrialDowntime: '36-48 hours',
      backupCapacity: 'limited',
    },
  },
  {
    id: 'H010',
    name: 'Alpine Snowstorm',
    type: 'natural',
    subtype: 'winter storm',
    lat: 47.3769,
    lng: 8.5417,
    severity: 'medium',
    description:
      'Severe snowstorm affecting transportation through Alpine regions',
    startDate: '2023-10-22',
    estimatedEndDate: '2023-10-26',
    radius: 150,
    affectedSuppliers: ['E009', 'E028', 'E029'],
    impactParameters: {
      snowfall: '75cm',
      roadClosures: 'extensive',
      avalancheRisk: 'high',
      temperatureDrop: '-15°C',
    },
  },
  {
    id: 'H011',
    name: 'Mediterranean Port Congestion',
    type: 'port',
    subtype: 'congestion',
    lat: 41.3851,
    lng: 2.1734,
    severity: 'low',
    description:
      'Seasonal congestion affecting Barcelona and surrounding ports',
    startDate: '2023-10-01',
    estimatedEndDate: '2023-11-10',
    radius: 80,
    affectedSuppliers: ['E010', 'E030', 'E032'],
    impactParameters: {
      vesselWaitTime: '36 hours',
      containerProcessingDelay: '3-4 days',
      operationalEfficiency: 'reduced by 25%',
    },
  },
  {
    id: 'H012',
    name: 'Eastern European Fuel Shortage',
    type: 'infrastructure',
    subtype: 'fuel supply',
    lat: 52.2297,
    lng: 21.0122,
    severity: 'medium',
    description:
      'Temporary fuel shortage affecting transportation in Eastern Europe',
    startDate: '2023-10-08',
    estimatedEndDate: '2023-10-28',
    radius: 250,
    affectedSuppliers: ['E011', 'E033', 'E034', 'E035', 'E036'],
    impactParameters: {
      fuelPriceIncrease: '35%',
      transportationCostIncrease: '20%',
      deliveryDelays: '24-72 hours',
    },
  },
]
